import { TranslationKey } from './../../../../types/translation/TranslationKey';
import React from 'react';
import { t } from '../../../../types/translation/Translator';
import { StockLocation } from '../../../../types/stock_location/stockLocation';
import { BinFilter } from '../BinFilter';
import { BinStatus } from '../../../../types/bin/binStatus';
import { Container } from '../../../../types/container';
import { Bin } from '../../../../types/bin/bin';
import { ContainerType } from '../../../../types/container_type/containerType';
import { ContainerTypeFilter } from './ContainerTypeFilter';
import { ContainerFilterContent, ContainerFilterInnerContent } from './DropdownContent';
import BoxIcon from '../../../icons/Box/BoxIcon';
import { BaseFilter, BaseFilterProps } from '../common/BaseFilter';
import { FilterStorageKey, FilterProps } from '../common/filter.util';
import { StockLocationFilter } from '../StockLocationFilter';
import { TagRelation } from '../../../../types/tag/tagRelation';
import { CustomFieldFilter } from '../CustomFieldFilter';
import { TagFilter } from '../TagFilter';
import { CustomField } from '../../../../types/custom_field/customField';
import { CustomFieldEntityType } from '../../../../types/custom_field';
import { EntityType } from '../../../../types/comment';
import { TableSettings } from '../../../../types/table_settings/TableSettings';
import { containerTableFilter } from '../../../../ui/components/Table/Container/ContainerTable';
import { Company } from '../../../../types/company';
import { CompanyPartnerFilter } from '../CompanyPartnerFilter/CompanyPartnerFilter';

export class ContainerFilter extends BaseFilter<Container> {
  stockLocations: Map<string, StockLocation>;
  bins: Map<string, Bin>;
  containerTypes: Map<string, ContainerType>;
  binStatuses: Map<string, BinStatus>;
  tagRelations: Map<string, TagRelation>;
  customFields: Map<string, CustomField>;
  containers: Map<string, Container>;
  tableSettings: TableSettings<any>;
  company: Company;

  constructor(
    stockLocations: Map<string, StockLocation>,
    bins: Map<string, Bin>,
    containerTypes: Map<string, ContainerType>,
    binStatuses: Map<string, BinStatus>,
    tagRelations: Map<string, TagRelation>,
    customFields: Map<string, CustomField>,
    containers: Map<string, Container>,
    tableSettings: TableSettings<any>,
    company: Company,
  ) {
    super(FilterStorageKey.CONTAINER);
    this.stockLocations = stockLocations;
    this.bins = bins;
    this.containerTypes = containerTypes;
    this.binStatuses = binStatuses;
    this.tagRelations = tagRelations;
    this.customFields = customFields;
    this.containers = containers;
    this.tableSettings = tableSettings;
    this.company = company;
  }

  filterOptions = () => [
    ...(this.company.partner ? [new CompanyPartnerFilter()] : []),
    new StockLocationFilter(this.tagRelations, this.company),
    new BinFilter(this.binStatuses, this.tagRelations, this.customFields, this.bins, this.company, this.stockLocations),
    new ContainerTypeFilter(),
    ...CustomFieldFilter.for(CustomFieldEntityType.bin, this.bins, this.customFields),
    ...CustomFieldFilter.for(CustomFieldEntityType.container, this.containers, this.customFields),
  ];

  toLabel(props: BaseFilterProps): string {
    return t(TranslationKey.container).singular.label;
  }

  toCount(props: BaseFilterProps): number {
    return props.filterProps.container?.size || 0;
  }

  toIcon(): React.JSX.Element {
    return <BoxIcon />;
  }

  toDropdownContent(props: BaseFilterProps): React.JSX.Element {
    return (
      <ContainerFilterContent filter={this} filterProps={props.filterProps} setFilterProps={props.setFilterProps} />
    );
  }

  toInnerContent(props: BaseFilterProps): string | JSX.Element {
    return (
      <ContainerFilterInnerContent
        filter={this}
        filterProps={props.filterProps}
        setFilterProps={props.setFilterProps}
      />
    );
  }

  search(item: Container, filter: string) {
    return containerTableFilter(
      item,
      filter,
      this.tableSettings,
      this.stockLocations,
      this.bins,
      this.containers,
      this.containerTypes,
    );
  }

  filteredItems(items: Container[], query: string, filterProps: FilterProps): Container[] {
    return items.filter(item => {
      const bin = this.bins.get(item.binId);

      return [
        StockLocationFilter.filter(filterProps, this.stockLocations.get(item.stockLocationId)),
        TagFilter.filterBy(filterProps, EntityType.stockLocation, this.tagRelations, item.stockLocationId),
        BinFilter.filter(filterProps, bin, this.customFields),
        ContainerFilter.filter(filterProps, item, this.customFields),
        this.search(item, query),
      ].every(value => value === null || value);
    });
  }

  static filter(filterProps: FilterProps, item?: Container, customFields: Map<string, CustomField> = new Map()) {
    if (item && filterProps.container?.size && !filterProps.container.has(item.id)) return false;
    if (item && filterProps.containerType?.size && !filterProps.containerType.has(item.containerTypeId)) return false;
    if (item && filterProps.company?.size && !filterProps.company.has(item.companyId)) return false;
    return CustomFieldFilter.filter(filterProps, item, customFields, CustomFieldEntityType.container);
  }
}
